import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { FileDown, Clock, AlertTriangle, Loader2 } from "lucide-react";
import { Seo } from "@/components/site/Seo";
import { Logo } from "@/components/site/Header";
import { api, API, errMsg } from "@/lib/api";
import { fmtSize } from "@/lib/site";

const Body = ({ token }) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ["share", token], retry: false,
    queryFn: async () => (await api.get(`/share/${token}`)).data,
  });
  if (isLoading) return <Loader2 className="h-6 w-6 animate-spin text-emerald-800" />;
  if (error) {
    return (
      <div className="space-y-3" data-testid="share-error">
        <AlertTriangle className="h-7 w-7 text-amber-700" />
        <h1 className="text-2xl font-semibold">Link not available</h1>
        <p className="text-stone-600">{errMsg(error, "This link is not valid.")}</p>
      </div>
    );
  }
  return (
    <div className="space-y-5" data-testid="share-doc">
      <p className="text-xs uppercase tracking-[0.2em] text-emerald-800">{data.doc_type}</p>
      <h1 className="text-2xl md:text-3xl font-semibold" data-testid="share-doc-title">{data.title}</h1>
      <p className="text-sm text-stone-500">{data.filename} · {fmtSize(data.size)}</p>
      <p className="flex items-center gap-2 text-sm text-stone-600" data-testid="share-doc-expiry">
        <Clock className="h-4 w-4" /> Link valid until {new Date(data.expires_at).toLocaleString()}
      </p>
      <a href={`${API}/share/${token}/download`} data-testid="share-download-btn"
        className="inline-flex items-center gap-2 rounded-full bg-emerald-900 hover:bg-emerald-800 text-white px-7 py-3.5 font-medium transition-colors">
        <FileDown className="h-4 w-4" /> Download document
      </a>
      <p className="text-xs text-stone-400">Downloads from this link are logged by SLN Agro Exports.</p>
    </div>
  );
};

export default function DocShare() {
  const { token } = useParams();
  return (
    <div className="min-h-screen bg-stone-100 flex flex-col">
      <Seo title="Shared document" noindex />
      <header className="px-6 md:px-12 py-6"><Logo /></header>
      <main className="flex-1 grid place-items-center px-6 pb-20">
        <div className="w-full max-w-lg rounded-lg border border-stone-200 bg-white p-8 md:p-12">
          <Body token={token} />
        </div>
      </main>
    </div>
  );
}
